import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import type { SupportCustomClaims } from "@srd/shared";
import { auth, googleProvider, startTotpMfaEnrollmentPlaceholder } from "../firebase";

interface AuthContextValue {
  user: User | null;
  claims: SupportCustomClaims | null;
  loading: boolean;
  authError: string | null;
  clearAuthError: () => void;
  signInGoogle: () => Promise<void>;
  signInEmail: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  startMfaPlaceholder: () => Promise<string>;
  refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function formatAuthError(err: unknown): string {
  if (!err || typeof err !== "object") return String(err);
  const e = err as { code?: string; message?: string };
  const code = e.code ?? "";
  const message = e.message ?? String(err);
  if (code === "auth/account-exists-with-different-credential") {
    return (
      "An account already exists with this email using a different sign-in method. " +
      "Ask an admin to delete the stub Auth user, then Sign in with Google again."
    );
  }
  if (code === "auth/unauthorized-domain") {
    return "This domain is not authorized for Firebase Auth. Add it under Authentication → Settings → Authorized domains.";
  }
  if (code === "auth/operation-not-allowed") {
    return "Google sign-in is disabled for this Firebase project. Enable it under Authentication → Sign-in method.";
  }
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
    return "Sign-in was cancelled before it finished. Allow popups for this site and try again.";
  }
  if (message.includes("redirect_uri_mismatch")) {
    return (
      "Google OAuth redirect_uri_mismatch: add " +
      "https://ets-remote-desktop.firebaseapp.com/__/auth/handler (and your portal /__/auth/handler) " +
      "under Google Cloud → APIs & Services → Credentials → OAuth 2.0 Web client."
    );
  }
  return code ? `${code}: ${message}` : message;
}

async function readSupportClaims(user: User): Promise<SupportCustomClaims | null> {
  const token = await user.getIdTokenResult(true);
  const role = token.claims.role;
  const tenantId = token.claims.tenantId;
  if (
    (role !== "technician" && role !== "admin" && role !== "auditor") ||
    typeof tenantId !== "string" ||
    !tenantId
  ) {
    return null;
  }
  return {
    role,
    tenantId,
    canUnattended: Boolean(token.claims.canUnattended),
    deviceGroups: Array.isArray(token.claims.deviceGroups)
      ? (token.claims.deviceGroups as unknown[]).filter((g): g is string => typeof g === "string")
      : undefined,
    mfaRequired:
      token.claims.mfaRequired === undefined ? true : Boolean(token.claims.mfaRequired),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<SupportCustomClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let redirectDone = false;
    let authSettled = false;

    function maybeFinishLoading() {
      if (!cancelled && redirectDone && authSettled) {
        setLoading(false);
      }
    }

    void (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (!cancelled && result?.user) {
          setUser(result.user);
          try {
            setClaims(await readSupportClaims(result.user));
          } catch {
            setClaims(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setAuthError(formatAuthError(err));
        }
      } finally {
        redirectDone = true;
        maybeFinishLoading();
      }
    })();

    const unsub = onAuthStateChanged(auth, (next) => {
      void (async () => {
        if (cancelled) return;
        setUser(next);
        if (!next) {
          setClaims(null);
        } else {
          try {
            setClaims(await readSupportClaims(next));
          } catch {
            setClaims(null);
          }
        }
        authSettled = true;
        maybeFinishLoading();
      })();
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      claims,
      loading,
      authError,
      clearAuthError: () => setAuthError(null),
      signInGoogle: async () => {
        setAuthError(null);
        try {
          // Popup avoids Chrome third-party storage breakage on redirect when
          // authDomain is *.firebaseapp.com and the app is on *.web.app / custom domain.
          // Do not fall back to redirect until OAuth client lists the hosting /__/auth/handler URIs.
          await signInWithPopup(auth, googleProvider);
        } catch (err) {
          setAuthError(formatAuthError(err));
          throw err;
        }
      },
      signInEmail: async (email, password) => {
        setAuthError(null);
        try {
          await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
          setAuthError(formatAuthError(err));
          throw err;
        }
      },
      signOutUser: async () => {
        await signOut(auth);
      },
      startMfaPlaceholder: startTotpMfaEnrollmentPlaceholder,
      refreshClaims: async () => {
        if (!auth.currentUser) return;
        setClaims(await readSupportClaims(auth.currentUser));
      },
    }),
    [user, claims, loading, authError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
